'use client';

import clsx from 'clsx/lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import { CaretIcon, MobileCollapsibleIcon } from '~/components/Icons';
import type { GfFieldChoice } from '~/types/graphql';

interface CustomDropdownProps {
  options: Array<{
    value?: string;
    label?: string;
    color?: string;
  }>;
  label: string;
  value: string;
  onChange: (value: string) => void;
  allLabel?: string | boolean;
  className?: string;
  collapsible?: boolean;
  multiple?: false;
}

// For cases where multiple selection is needed, create a separate interface
interface MultiCustomDropdownProps
  extends Omit<CustomDropdownProps, 'value' | 'onChange' | 'multiple'> {
  value: string[];
  onChange: (value: string[]) => void;
  multiple: true;
}

// Use union type to allow either single or multiple selection
type CustomDropdownAllProps = CustomDropdownProps | MultiCustomDropdownProps;

export const CustomDropdown = ({
  options,
  label,
  value,
  onChange,
  allLabel = 'All',
  className = '',
  collapsible = false,
  multiple = false,
}: CustomDropdownAllProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState<number | null>(null);

  const hasColor = options.some((option) => option.color);

  // Convert value to array for consistent handling
  const selectedValues = useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value : [];
    }
    return typeof value === 'string' ? [value] : [];
  }, [value, multiple]);

  useEffect(() => {
    const calculateWidth = () => {
      if (dropdownRef.current && window.innerWidth > 991) {
        const listItems = dropdownRef.current.querySelectorAll('li');
        let maxWidth = 0;

        listItems.forEach((li) => {
          const span = li.querySelector('span');
          if (span) {
            const width = span.getBoundingClientRect().width;
            maxWidth = Math.max(maxWidth, width);
          }
        });

        setDropdownWidth(maxWidth);
      } else {
        setDropdownWidth(null); // Reset width for mobile viewports
      }
    };

    calculateWidth();

    // Add resize event listener
    window.addEventListener('resize', calculateWidth);

    // Cleanup
    return () => {
      window.removeEventListener('resize', calculateWidth);
    };
  }, [isOpen]);

  const handleOptionClick = (optionValue: string) => {
    if (multiple) {
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];
      (onChange as (value: string[]) => void)(newValues);
    } else {
      (onChange as (value: string) => void)(
        optionValue === 'All' ? '' : optionValue,
      );
      setIsOpen(false);
    }
  };

  const selectedLabel = useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) return label;

    if (multiple) {
      // if (selectedValues.length === 1) {
      //   const option = options.find((opt) => opt.value === selectedValues[0]);
      //   return option ? option.label : label;
      // }
      // return `${selectedValues.length} Selected`;
      return label;
    }

    const selectedOption = options.find((option) => option.value === value);
    return selectedOption ? selectedOption.label : label;
  }, [value, options, label, multiple]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={clsx(
        'relative min-w-min tracking-[1.5px] lg:w-min',
        className,
        isOpen && 'is-open',
      )}
      style={{ width: dropdownWidth ? `${dropdownWidth}px` : '100%' }}
    >
      {collapsible && (
        <button
          className={clsx(
            'absolute right-0 top-[-28px] rounded border px-3 py-2 lg:hidden',
            isOpen ? 'border-black' : 'border-[#7A7A7A] hover:border-black',
          )}
          onClick={() => {
            setIsOpen(false);
            setShowFilters(!showFilters);
          }}
        >
          <MobileCollapsibleIcon open={showFilters} />
        </button>
      )}
      <button
        className={clsx(
          'flex h-12 w-full min-w-fit items-center border text-left lg:w-full',
          isOpen ? 'is-open rounded-t' : 'rounded',
          isOpen ? 'border-black' : 'border-[#7A7A7A] hover:border-black',
          !showFilters && collapsible && 'max-lg:hidden',
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span
          className={clsx(
            'whitespace-nowrap px-3 font-nova text-[14px] font-semibold uppercase lg:pl-6 lg:pr-8 lg:text-[20px]',
            hasColor && 'justify-end',
          )}
        >
          {selectedLabel}
        </span>
        <CaretIcon
          className={clsx(
            'absolute right-3 top-1/2 -translate-y-1/2 transition-all duration-500',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      <ul
        className={clsx(
          'dropdown-list absolute z-50 -mt-px w-full overflow-hidden rounded-b border-x border-b bg-white',
          isOpen
            ? 'h-auto max-h-[50vh] overflow-y-auto !border-t-[#ABABAB]'
            : 'collapse',
        )}
      >
        {allLabel && !multiple && (
          <li
            className={clsx(
              'flex h-12 cursor-pointer items-center border-t border-t-[#ABABAB] text-[14px] font-bold uppercase',
              hasColor && 'justify-end',
            )}
            onClick={() => handleOptionClick('All')}
            data-value="All"
          >
            <span className="whitespace-nowrap px-3 font-nova text-[14px] font-semibold uppercase lg:px-6 lg:text-[20px]">
              {allLabel}
            </span>
          </li>
        )}
        {options.map((option, i) => (
          <li
            key={i}
            className={clsx(
              'flex h-12 cursor-pointer items-center border-t border-t-[#ABABAB] text-[14px] font-bold uppercase',
              hasColor && 'justify-end',
              selectedValues.includes(option.value || '') && 'bg-gray-100',
            )}
            onClick={() => {
              if (option.value) handleOptionClick(option.value);
            }}
            data-value={option.value}
          >
            <span className="label flex items-center whitespace-nowrap px-3 font-nova text-[14px] font-semibold uppercase lg:px-6 lg:text-[20px]">
              {multiple && (
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value || '')}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (option.value) handleOptionClick(option.value);
                  }}
                  className="mr-4 h-4 w-4 cursor-pointer appearance-none rounded border border-[#7A7A7A] checked:relative checked:before:absolute checked:before:inset-[2px] checked:before:block checked:before:rounded-sm checked:before:bg-black hover:border-black"
                />
              )}
              {option.label}
              {option.color && (
                <span
                  className="ml-2 inline-block h-3 w-3 rounded-full"
                  style={{ backgroundColor: option.color }}
                ></span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

type GFDropdownProps = Omit<ControllerRenderProps, 'onChange'> & {
  onChange: (value: string) => void;
  choices: GfFieldChoice[];
  isRequired?: boolean;
  hasError?: boolean;
  className?: string;
};

export const GFDropdown = ({
  ref,
  name,
  choices,
  value,
  onChange,
  className,
  isRequired,
  hasError,
}: Omit<GFDropdownProps, 'options'> & {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={clsx(`relative w-full`, className, isOpen && 'is-open')}
    >
      <input
        ref={ref}
        type="hidden"
        id={name}
        name={name}
        value={value}
        required={isRequired}
        // placeholder={placeholder || field.placeholder}
        aria-invalid={hasError}
        aria-required={isRequired}
      />
      <button
        className={clsx(
          'relative flex h-[70px] w-full items-center border border-[--text-color] px-5 font-roboto text-[13px] font-normal leading-[19px]',
          hasError ? 'border-[#DB1500]' : 'border-[--text-color]',
          isOpen ? 'is-open rounded-t-[--radius]' : 'rounded-[--radius]',
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="whitespace-nowrap">{value}</span>
        <CaretIcon className="absolute right-3 top-1/2 -translate-y-1/2" />
      </button>
      {isOpen && (
        <ul className="dropdown-list absolute z-10 -mt-[2px] w-full rounded-b-[--radius] border-x border-b bg-white">
          {choices.map((choice, i) => (
            <li
              key={i}
              className="flex h-[70px] cursor-pointer items-center border-t border-t-[#ABABAB] px-5 font-roboto text-[13px] font-normal leading-[19px]"
              onClick={() => handleOptionClick(choice.value ?? '')}
              data-value={choice.value}
            >
              <span className="whitespace-nowrap">{choice.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
